/**
 * Worker initials row shown inside schedule scope bars while the timeline is in Workers view.
 */
import { useState } from "react";
import { WorkerInitials } from "../../../../components/ui";

/** Must stay in sync with {@link WorkerInitials} default badge size when not overridden via style. */
const INITIAL_SIZE_PX = 22;
/** Matches parent label padding (`pl-2` / `-ml-2`) inside the scope bar. */
const ROW_LEFT_OFFSET_PX = 8;
const DEFAULT_MARGIN_RIGHT_PX = 2;
const OVERRIDE_MARGIN_RIGHT_PX = -2;
const OVERRIDE_TOP_OFFSET_PX = 12;
const OVERRIDE_SCALE = 0.8;
const STACK_BG_VERTICAL_PADDING_PX = 3;
const STACK_BG_HORIZONTAL_PADDING_PX = 4;
const EXPANDED_BADGE_GAP_PX = 2;

/**
 * @param {boolean} overridePresent
 * @returns {number}
 */
function getBadgeScale(overridePresent) {
  return overridePresent ? OVERRIDE_SCALE : 1;
}

/**
 * @param {boolean} overridePresent
 * @param {number} marginRightPx
 * @param {Object} [extra]
 * @returns {Object}
 */
function getBadgeStyle(overridePresent, marginRightPx, extra = {}) {
  const scale = getBadgeScale(overridePresent);
  return {
    width: INITIAL_SIZE_PX,
    height: INITIAL_SIZE_PX,
    marginRight: marginRightPx,
    flexShrink: 0,
    ...(scale === 1
      ? {}
      : {
        transform: `scale(${scale})`,
        transformOrigin: "left center",
      }),
    ...extra,
  };
}

/**
 * Renders the default block crew initials for a schedule ScopeBar.
 *
 * @param {Object} props
 * @param {Array<Object>} props.workers Block-level default worker assignment.
 * @param {boolean} props.overridePresent Whether the bar has an override strip.
 * @param {number} props.scopeBarWidth Rendered width of the parent ScopeBar.
 * @param {string} props.scopeLabel Scope name shown when there is no default crew.
 * @param {boolean} [props.clippedStart] Whether the bar start is clipped off-screen.
 * @param {boolean} [props.clippedEnd] Whether the bar end is clipped off-screen.
 * @param {(expanded: boolean) => void} [props.onExpandedChange] Notifies the parent bar to raise above sibling bars.
 * @returns {JSX.Element}
 */
export default function ScopeBarWorkerInitialsRow({
  workers,
  overridePresent,
  scopeBarWidth,
  scopeLabel,
  clippedStart = false,
  clippedEnd = false,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useState(false);

  if (!workers.length) {
    return (
      <span
        className="min-w-0 flex-1 truncate"
        style={overridePresent ? { marginTop: OVERRIDE_TOP_OFFSET_PX } : {}}
      >
        {clippedStart ? "…" : ""}
        {scopeLabel}
        {clippedEnd ? "…" : ""}
      </span>
    );
  }

  const marginRightPx = overridePresent
    ? OVERRIDE_MARGIN_RIGHT_PX
    : DEFAULT_MARGIN_RIGHT_PX;
  const expandedBadgeWidthPx = INITIAL_SIZE_PX;
  const expandedBadgeStepPx = expandedBadgeWidthPx + EXPANDED_BADGE_GAP_PX;
  const expandedNaturalWidthPx =
    workers.length * expandedBadgeWidthPx +
    Math.max(0, workers.length - 1) * EXPANDED_BADGE_GAP_PX;
  const availableWidthPx = Math.max(0, scopeBarWidth - ROW_LEFT_OFFSET_PX);
  const shouldStack = expandedNaturalWidthPx > availableWidthPx && workers.length > 1;
  const stackedStepPx = shouldStack
    ? Math.max(0, (availableWidthPx - INITIAL_SIZE_PX) / (workers.length - 1))
    : 0;
  const rowWidthPx = shouldStack && expanded
    ? expandedNaturalWidthPx + STACK_BG_HORIZONTAL_PADDING_PX * 2
    : availableWidthPx;

  const rowOffsetStyle = {
    marginLeft: expanded ? -12 : -ROW_LEFT_OFFSET_PX,
    ...(overridePresent ? { marginTop: expanded ? 0 : OVERRIDE_TOP_OFFSET_PX } : {}),
  };

  /**
   * @param {boolean} nextExpanded
   * @returns {void}
   */
  const setExpandedState = (nextExpanded) => {
    setExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  return (
    <span
      className={
        shouldStack
          ? "pointer-events-auto relative z-50 block shrink-0 rounded"
          : "flex min-w-0 flex-1 items-center"
      }
      style={{
        ...rowOffsetStyle,
        ...(shouldStack
          ? {
            width: rowWidthPx,
            maxWidth: rowWidthPx,
            height: INITIAL_SIZE_PX + STACK_BG_VERTICAL_PADDING_PX * 2,
            paddingTop: STACK_BG_VERTICAL_PADDING_PX,
            paddingBottom: STACK_BG_VERTICAL_PADDING_PX,
            paddingLeft: expanded ? STACK_BG_HORIZONTAL_PADDING_PX : 0,
            paddingRight: expanded ? STACK_BG_HORIZONTAL_PADDING_PX : 0,
            backgroundColor: expanded
              ? "rgb(89 133 103)"
              : "rgb(13 148 136 / 0)",
            transition: "width 150ms ease, max-width 150ms ease, background-color 150ms ease, padding 150ms ease",
          }
          : {}),
      }}
      onMouseEnter={shouldStack ? () => setExpandedState(true) : undefined}
      onMouseLeave={shouldStack ? () => setExpandedState(false) : undefined}
    >
      {workers.map((worker, index) => (
        <WorkerInitials
          key={worker.id}
          worker={worker}
          className={shouldStack ? "absolute" : ""}
          style={getBadgeStyle(
            overridePresent && !expanded,
            shouldStack ? 0 : marginRightPx,
            shouldStack
              ? {
                top: STACK_BG_VERTICAL_PADDING_PX,
                left: (expanded ? STACK_BG_HORIZONTAL_PADDING_PX : 0) +
                  index * (expanded ? expandedBadgeStepPx : stackedStepPx),
                zIndex: 30 + (index + 1),
                transition: "left 150ms ease, transform 150ms ease",
              }
              : {}
          )}
          colorByRole
          darken={0.15}
        />
      ))}
    </span>
  );
}
